import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { compose } from 'redux';
import { Set } from 'immutable';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { nanoid } from 'nanoid';
import moment from 'moment';
import {
  documentUploadAction,
  queueAddAction,
  queueRemoveAction,
  isPendingUploadSelector,
  uploadQueueSelector,
} from 'domain/documents';
import { paramsAdapter, uploadQueueItemAdapter } from 'domain/documents/adapters';
import { currentCompanyDateFormatSelector } from 'domain/companies';
import { rootCategoriesListSelector } from 'domain/categories';
import {
  isCategoryAll,
  isToApproveCategory,
  isPublishFailedCategory,
  isTrashCategoryById,
} from 'domain/categories/helpers';
import { chatAllUsersByIdSelector } from 'domain/chat';
import * as ACL from 'domain/restriction';
import acceptTypes from 'lib/acceptTypes';
import {
  checkFiles,
  checkFileSize,
  SelectedCategoryFactory,
  selectedCategoryAdapter,
} from 'pages/common/Dialog/DialogUploadDocuments/helpers';

import Queue from 'pages/common/Dialog/DialogUploadDocuments/queue';
import Dropzone from 'pages/common/Dialog/DialogUploadDocuments/dropzone';
import Dialog from 'components/mui/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import InputAdornment from '@mui/material/InputAdornment';
import SelectBase from 'components/mui/Form/Select/SelectBase';
import { UploadText, FileInput } from 'pages/common/Dialog/DialogUploadDocuments/StyledComponents';
import toast from 'components/Toast';
import TagSuggestionsAutocomplete from 'components/mui/Layouts/components/TagSuggestionsAutocomplete';
import TagsSearchInput from 'pages/common/Dialog/DialogTagsManage/components/TagsSearchInput';
import TagBox from 'pages/common/Dialog/DialogTagsManage/components/TagBox';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import { lightBlue } from '@mui/material/colors';
import elements from 'components/elements';

const isAllowedCategory = (category) => {
  return (
    !isCategoryAll(category) &&
    !isToApproveCategory(category) &&
    !isPublishFailedCategory(category) &&
    !isTrashCategoryById(category)
  );
};

const checkFileValidity = (file, existingFileNames) => {
  const { name, type, size } = file;
  const splittedName = name.split('.');
  const fileType = type.length > 0 ? type : `.${splittedName[splittedName.length - 1]}`;

  return {
    isValidType: checkFiles(acceptTypes, fileType),
    isValidSize: checkFileSize(size),
    isUniqueName: !existingFileNames.includes(name),
  };
};

const useComponentSelectors = () =>
  useSelector((state) => ({
    queue: uploadQueueSelector(state),
    isPendingUpload: isPendingUploadSelector(state),
    rootCategoriesList: rootCategoriesListSelector(state),
    isGranted: ACL.isGranted(state),
    dateFormat: currentCompanyDateFormatSelector(state),
    userList: chatAllUsersByIdSelector(state),
  }));

const DialogUploadDocuments = ({ closeModal, confidential = false, open, items = null, linkToDocument }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { isPendingUpload, rootCategoriesList, isGranted, dateFormat, queue, userList } = useComponentSelectors();

  const fileInputElRef = useRef(null);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [inputTag, setInputTag] = useState('');
  const [currentTags, setCurrentTags] = useState(new Set());
  const [day, setDay] = useState(moment(new Date()));
  const [isFileDialogActive, setIsFileDialogActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const isUser = useMemo(() => isGranted(ACL.IS_USER), [isGranted]);

  const categoryOptions = useMemo(
    () =>
      rootCategoriesList.filter(isAllowedCategory).map((category) => {
        const { label, value } = selectedCategoryAdapter({
          label: {
            id: `category.name.${category.nameLangId}`,
            defaultMessage: category.name,
          },
          value: category.id,
        });
        return { label: formatMessage(label), value };
      }),
    [formatMessage, rootCategoriesList],
  );

  const uploadParams = useMemo(() => {
    const baseParams = { creationDate: day, linkToDocument };
    return isUser ? baseParams : { ...baseParams, category: selectedCategory };
  }, [day, isUser, selectedCategory, linkToDocument]);

  const removeQueue = useCallback((props) => dispatch(queueRemoveAction(props)), [dispatch]);

  const clearQueueOnClose = useCallback(() => {
    if (queue.size) {
      const filteredQueue = queue.filter((i) => i.status !== 'uploading');
      removeQueue(filteredQueue.keySeq().toArray());
    }
  }, [queue, removeQueue]);

  const handleClose = useCallback(() => {
    setDay(moment(new Date()));
    setSelectedCategory(categoryOptions.first(SelectedCategoryFactory()).value);
    clearQueueOnClose();
    closeModal();
  }, [closeModal, categoryOptions, clearQueueOnClose]);

  const createQueueItem = useCallback(
    (file, path) => {
      const id = nanoid(10);
      const existingFileNames = queue
        .valueSeq()
        .map((item) => item.file?.name)
        .filter(Boolean)
        .toArray();
      const { isValidType, isValidSize, isUniqueName } = checkFileValidity(file, existingFileNames);
      const isValidFile = isValidType && isValidSize && isUniqueName;
      const dispatchAdd = (args) => dispatch(queueAddAction(args));

      if (isValidFile) {
        dispatch(
          queueAddAction(
            uploadQueueItemAdapter({
              file,
              id,
              path,
              isConfidential: confidential,
              params: uploadParams,
            }),
          ),
        );
      } else if (!isValidType) {
        compose(dispatchAdd, uploadQueueItemAdapter)({ path, status: 'unsupportedType', id });
      } else if (!isValidSize) {
        compose(dispatchAdd, uploadQueueItemAdapter)({ path, status: 'maxFileSizeError', id });
      }
    },
    [confidential, dispatch, uploadParams, queue],
  );

  const processFileTransfer = useCallback(
    (item, path = '') => {
      if (!item) return;

      if (item.isFile) {
        item.file((file) => createQueueItem(file, `${path}${file.name}`));
      } else if (item.isDirectory) {
        const dirReader = item.createReader();
        dirReader.readEntries((entries) => {
          entries.forEach((entry) => {
            processFileTransfer(entry, `${path}${item.name}/`);
          });
        });
      }
    },
    [createQueueItem],
  );

  const processDragAndDrop = useCallback(
    (item, path = '') => {
      if (item) {
        createQueueItem(item, `${path}${item.name}`);
      }
    },
    [createQueueItem],
  );

  const handleDrop = useCallback(
    (e) => {
      if (isFileDialogActive || !e.dataTransfer?.items) return;

      const { items } = e.dataTransfer;
      Array.from(items).forEach((item) => {
        if (typeof item.webkitGetAsEntry === 'function') {
          processFileTransfer(item.webkitGetAsEntry());
        }
      });
    },
    [isFileDialogActive, processFileTransfer],
  );

  const handleDndDrop = useCallback(
    (files) => {
      if (isFileDialogActive || !files) return;

      Array.from(files).forEach(processDragAndDrop);
    },
    [processDragAndDrop, isFileDialogActive],
  );

  const handleFileInputChange = useCallback(
    (e) => {
      const { files } = e.target;
      Array.from(files).forEach((file) => {
        createQueueItem(file, file.name);
      });

      if (isFileDialogActive) {
        setIsFileDialogActive(false);
      }
      // allows onChange to fire when previously selected file is selected again
      e.target.value = '';
    },
    [createQueueItem, isFileDialogActive],
  );

  const handleCategoryChange = useCallback(
    ({ target: { value } }) => {
      if (value) {
        const currentCategory = categoryOptions.find(({ value: catValue }) => (catValue || '') === value);

        setSelectedCategory(currentCategory.value);
      }
    },
    [categoryOptions],
  );

  const handleDateChange = useCallback((newDay) => {
    setDay(newDay);
  }, []);

  const handleFileDialogClick = useCallback(() => {
    setIsFileDialogActive(true);
  }, []);

  const isUserTag = useCallback((tag) => +!!userList.get(tag), [userList]);

  const isTagAddForbidden = useCallback(
    (tag) => {
      const currentTag = tag || inputTag;

      return (
        currentTags.includes(currentTag) || !currentTag || (typeof currentTag === 'string' && currentTag[0] === '_')
      );
    },
    [currentTags, inputTag],
  );

  const sortTags = useCallback(
    (newTags) => {
      const tags = newTags || currentTags;

      return tags
        .toList()
        .sort((tagA, tagB) => isUserTag(tagB) - isUserTag(tagA))
        .toSet();
    },
    [isUserTag, currentTags],
  );

  const handleTagInputChange = useCallback((tag) => {
    setInputTag(tag);
  }, []);

  const handleAddTag = useCallback(
    (tag) => {
      if (!isTagAddForbidden(tag)) {
        const newCurrentTags = currentTags.add(tag);

        setCurrentTags(newCurrentTags);
      }

      setInputTag('');
    },
    [currentTags, isTagAddForbidden],
  );

  const handleDeleteTag = useCallback(
    (tag) => {
      const newCurrentTags = currentTags.delete(tag);

      setCurrentTags(newCurrentTags);
    },
    [currentTags],
  );

  const getTagsForUpload = useCallback(() => {
    if (inputTag && !isTagAddForbidden(inputTag)) {
      const tags = sortTags(currentTags.add(inputTag));

      handleAddTag(inputTag);

      return sortTags(tags);
    }

    return sortTags();
  }, [inputTag, isTagAddForbidden, currentTags, sortTags, handleAddTag]);

  const uploadFromQueue = useCallback(() => {
    const tags = getTagsForUpload();

    setIsUploading(true);

    toast.success(
      formatMessage({
        id: 'toast.document.file.upload.success',
        defaultMessage: 'Document upload in progress. Refresh page or check your company workspace for progress.',
      }),
    );

    queue.forEach((item) => {
      if (item.status === 'pending') {
        dispatch(documentUploadAction(item.set('params', paramsAdapter(uploadParams)).set('tags', tags)));
      }
    });
  }, [getTagsForUpload, uploadParams, queue, dispatch, formatMessage]);

  const reloadItem = useCallback(
    (item) => {
      if (item.file instanceof File) {
        const tags = getTagsForUpload();

        dispatch(documentUploadAction(item.set('tags', tags)));
      }
    },
    [dispatch, getTagsForUpload],
  );

  useEffect(() => {
    const defaultCategory = categoryOptions.first(SelectedCategoryFactory())?.value;

    if (defaultCategory) {
      setSelectedCategory(defaultCategory);
    }
  }, [categoryOptions]);

  useEffect(() => {
    if (isUploading && !queue.size) {
      handleClose();
    }
  }, [queue.size, handleClose, isUploading]);

  useEffect(() => {
    // When we set a selected category, the component re-renders,
    // and the file dropped onto the workspace is added twice.
    // This happens because the webkitGetAsEntry function is asynchronous,
    // and we cannot confirm that the file has been processed until the operation completes.
    // Therefore, you have to be careful with re-rendering
    if (items && selectedCategory) {
      items.valueSeq().forEach((item) => {
        if (typeof item.webkitGetAsEntry === 'function') {
          processFileTransfer(item.webkitGetAsEntry());
        } else {
          processDragAndDrop(item);
        }
      });
    }
  }, [items, processDragAndDrop, processFileTransfer, selectedCategory]);

  const accept = acceptTypes.join(', ');

  const uploaderBody = (
    <>
      <Box borderRadius="50%" bgcolor={lightBlue[50]} p={1} dispaly="flex" lineHeight={0}>
        <UploadFileIcon color="primary" />
      </Box>
      <Typography variant="subtitle1">
        {formatMessage({ id: 'document.upload.dropFilesToUploadOr', defaultMessage: 'Drop files to upload ' })}
        <UploadText>
          {formatMessage({ id: 'document.upload.chooseFromComputer', defaultMessage: 'or choose from computer' })}
        </UploadText>
      </Typography>
      <Typography variant="caption" color="text.disabled" textAlign="center">
        {formatMessage({ id: 'document.upload.maxSize', defaultMessage: 'The maximum file size supported is 64 MB' })}{' '}
        <br />
        {formatMessage({
          id: 'document.upload.weSupportFormats',
          defaultMessage:
            '.jpg, .jpeg, .png, .tiff, .tif, .bmp, .docx, .doc, .xlsx, .xls, .xlsm, .pptx, .pdf, .txt, .xml',
        })}
      </Typography>
      <FileInput
        type="file"
        hidden
        ref={fileInputElRef}
        accept={accept}
        id="dropzone"
        multiple
        onChange={handleFileInputChange}
        onClick={handleFileDialogClick}
      />
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={formatMessage({ id: 'document.upload.uploadDocument', defaultMessage: 'Upload Documents' })}
      maxWidth="sm"
      withActions={false}
      withContent={false}
      slotProps={{ paper: { sx: { overflow: 'inherit' } } }}
      data-element={elements.popup.Upload.dialog}
    >
      <DialogContent>
        <Dropzone htmlFor="dropzone" onDndDrop={handleDndDrop} onDrop={handleDrop}>
          {uploaderBody}
        </Dropzone>
        {!queue.size ? (
          <Divider sx={{ mt: 2, mb: 3 }} />
        ) : (
          <Queue queue={queue} remove={removeQueue} reload={reloadItem} />
        )}
        <Stack direction="row" spacing={2} mb={1}>
          {!isUser && (
            <SelectBase
              id="uploadCategory"
              value={selectedCategory}
              options={categoryOptions}
              onChange={handleCategoryChange}
              fullWidth
              FormControlProps={{ fullWidth: true }}
              label={formatMessage({ id: 'document.upload.uploadToDefaultCategory', defaultMessage: 'Upload to:' })}
              startAdornment={
                <InputAdornment position="start">
                  <MoveToInboxIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              }
            />
          )}
          <DesktopDatePicker
            label={formatMessage({ id: 'document.upload.uploadByDate', defaultMessage: 'Upload Date:' })}
            format={dateFormat}
            value={day}
            onChange={handleDateChange}
            disableFuture
            slotProps={{
              textField: { fullWidth: true, inputProps: { 'data-element': elements.popup.Upload.datePicker.input } },
              inputAdornment: { position: 'start', 'data-element': elements.popup.Upload.datePicker.iconContainer },
            }}
          />
        </Stack>
        <TagSuggestionsAutocomplete
          inputValue={inputTag}
          onInputChange={handleTagInputChange}
          onSelect={handleAddTag}
          prefixOutputWithHashtag={false}
          renderInput={(params) => (
            <TagsSearchInput
              params={params}
              disabled={isTagAddForbidden()}
              onAdd={() => handleAddTag(inputTag)}
              autoFocus={false}
              label={formatMessage({ id: 'labels.documentTags', defaultMessage: 'Tag Documents' })}
            />
          )}
        />
        <Stack gap={0.5} flexWrap="wrap" direction="row" mt={1}>
          {sortTags().map((tag) => (
            <TagBox key={tag} tag={tag} onDelete={handleDeleteTag} />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="text" data-element={elements.popup.Upload.cancelBtn}>
          {formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
        </Button>
        <Button
          onClick={uploadFromQueue}
          disabled={!queue.size || !isPendingUpload}
          data-element={elements.popup.Upload.uploadBtn}
        >
          {formatMessage({ id: 'input.upload', defaultMessage: 'Upload' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogUploadDocuments;
